// Hints for the build mode (PRD section 19.7): which way a belt drawn with the finger most likely
// means to go. They read the interior's sockets exactly as the flow network does, so a hinted belt
// always connects to what the simulation will connect it to.
import { keyOf, neighbour, opposite, rotateSide, sideTowards, SIDES, type Cell, type Side } from './geometry';
import { downstream, interiorSockets, socketKey, type InteriorSockets } from './network';
import { machineOutput, type TileState, type World } from './world';

/** The side material enters `cell` through from what already feeds it: an entrance, a machine output or a belt. */
function feederSide(tile: TileState, sockets: InteriorSockets, cell: Cell, ignore: ReadonlySet<string>): Side | undefined {
  for (const side of SIDES) if (sockets.entrances.has(socketKey({ cell, side }))) return side;
  const interior = tile.interior;
  if (!interior) return undefined;
  for (const machine of interior.machines.values()) {
    const out = machineOutput(machine);
    if (keyOf(neighbour(out.cell, out.side)) === keyOf(cell)) return opposite(out.side);
  }
  for (const side of SIDES) {
    const from = neighbour(cell, side);
    const key = keyOf(from);
    if (!ignore.has(key) && interior.belts.get(key) === opposite(side)) return side;
  }
  return undefined;
}

/**
 * The exit side for the last cell of a belt about to be drawn along `path`: straight on, or a turn,
 * towards the first thing that would take its material (an exit door, a machine input or another
 * belt), never back into the belt itself. Without anything to connect to it keeps going straight on
 * (or away from what feeds a single cell); undefined when a single cell has no clue at all.
 */
export function beltExitHint(world: World, tileId: string, path: readonly Cell[]): Side | undefined {
  const tile = world.tiles.get(tileId);
  const last = path[path.length - 1];
  if (!tile?.interior || !last) return undefined;
  const onPath = new Set(path.map(keyOf));
  const sockets = interiorSockets(tile);
  const prev = path[path.length - 2];
  const back = prev ? sideTowards(last, prev) : feederSide(tile, sockets, last, onPath);
  const straight = back ? opposite(back) : undefined;
  const candidates = straight ? [straight, rotateSide(straight, 1), rotateSide(straight, 3)] : SIDES;
  for (const side of candidates) {
    const to = downstream(sockets, { cell: last, side });
    if (!to) continue;
    if ('exit' in to) return side;
    // The belts under the new path are replaced by it: feeding one of them would close a loop.
    if (to.receiver.kind === 'belt' && onPath.has(to.receiver.key)) continue;
    return side;
  }
  return straight;
}
